import {
  Box,
  Button,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ButtonPanel } from "../../../components/button-panel";
import { ErrorPanel } from "../../../components/error-panel";
import { HeavyCard } from "../../../components/heavy-card";
import { HttpErrorPanel } from "../../../components/http-error";
import { Loading } from "../../../components/loading";
import { useQuestionState } from "../question/question.context";
import { BallotContext } from "./ballot.context";
import { useNavigate, useParams } from "react-router-dom";
import { ElectionContext } from "../election.context";
import { prepareBallotSubmission, prepareQuestionSubmission } from "./util";
import { ReviewMux } from "../../../question-types/review-mux";
import PersonIcon from '@mui/icons-material/Person';
import { ApiIQuestion } from "../../../domain/election";
import { AxiosResponse } from "axios";

export const BallotReview = () => {
  const { election_id } = useParams();

  const { ballot, questions, ballotState, clearBallotState } =
    useContext(BallotContext);
  const { submitBallot, election, voter } = useContext(ElectionContext);
  const navigate = useNavigate();

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<AxiosResponse | null>(null);
  const done = useMemo(() => !!result && result.status === 200, [result]);

  useEffect(() => {
    if (done) {
      setTimeout(() => navigate(`/voting/${election_id}`), 1000);
    }
  }, [done, navigate, ballot]);

  if (!voter) {
    return <ErrorPanel text="Must have consumer" source="ReviewPage" />;
  }

  const handleSubmit = () => {
    setSubmitting(true);
    submitBallot(prepareBallotSubmission(ballotState, election.voting_payload_cache_id)).then((res) => {
      clearBallotState();
      setResult(res);
    });
    // setTimeout(() => submit().then((r) => setResult(r)), 200);
  };

  const { opt_in } = ballotState;

  const handleBack = () => {
    setSubmitting(false);
  };

  if (result && result.status !== 200 && "response" in result) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h4">Review</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>
            Please review your submission before submitting. After submission
            you will not be able to change your vote.
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h4">Review</Typography>
        </Grid>
        <Grid item xs={12}>
          <HttpErrorPanel
            text="Submit failed"
            source="ReviewPage"
            axiosResponse={result}
          />
        </Grid>
        <Grid item xs={12}>
          <Button onClick={handleBack} variant="outlined">
            Back
          </Button>
        </Grid>
      </Grid>
    );
  }

  if (submitting || done) {
    return (
      <Grid container>
        <Grid item xs={12}>
          <Typography variant="h4">Review</Typography>
        </Grid>
        <Grid item xs={12}>
          <Typography>Thank you for participating in this election.</Typography>
        </Grid>
        <Grid item xs={12} mt={4}>
          <Loading source="Review" waitingFor={{ Submit: done }} />
          {done ? (
            <Typography
              color="green"
              sx={{ display: "flex", justifyContent: "center" }}
            >
              Submitted!
            </Typography>
          ) : null}
        </Grid>
      </Grid>
    );
  }
  return (
    <Grid container>
      <Grid item xs={12}>
        <Typography variant="h4" >{ballot.label}</Typography>
        <Typography variant="h5" mb={4}>Submission Review</Typography>
      </Grid>
      <Grid item xs={12}>
        <ProxyReview />
      </Grid>
      <Grid item xs={12}>
        {opt_in === false ?
          <HeavyCard sx={{ p: 3 }}>
            <Typography>You have chosen to opt out of this ballot. You may submit your opt-out feedback.</Typography>
          </HeavyCard>
          :
          questions.map((q) => (
            <QuestionReview question={q} key={q.id} />
          ))}
      </Grid>
      <Grid item xs={12} mt={3}>
        <ButtonPanel
          back={{
            to: `/voting/${voter.election_id}/ballot/${ballot.id}/vote`,
          }}
          next={{
            label: "Submit",
            icon: "submit",
            onClick: handleSubmit,
          }}
        />
      </Grid>
    </Grid>
  );
};

const ProxyReview = () => {

  const { proxyVotes, ballot, submitState } = useContext(BallotContext);
  const { proxy_votes } = submitState;

  const _proxyVotes = useMemo(() => {
    if (!proxy_votes || !proxyVotes) {
      return null;
    }
    return proxy_votes.filter((v) => proxyVotes.includes(v.voter_id));
  }, [proxy_votes, proxyVotes]);

  if (!_proxyVotes) {
    return null;
  }
  return (
    <Box sx={{ p: 0 }}>
      <Box sx={{
        mb: 6,
        p: 3,
        background: "rgb(238, 246, 250)",
        color: "rgb(3, 49, 71)"
      }}>
        <Typography variant="h6">Proxy Votes</Typography>
        <Table size="small">
          <TableBody>
            {_proxyVotes.map((r, i) => (
              <TableRow key={i}>
                <TableCell align="center" width={20}>
                  <PersonIcon />
                </TableCell>
                <TableCell>
                  <Typography>{r.label}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
interface QuestionReviewProps {
  question: ApiIQuestion
}
const QuestionReview = ({ question }: QuestionReviewProps) => {
  const [questionState] = useQuestionState(question.id);
  const { value } = questionState;

  return (
    <HeavyCard title={question.label} sx={{ mb: 3 }}>
      {value ? (
        <ReviewMux
          question={question}
          response={value}
        />
      ) : (
        <Typography color="error">No vote (blank)</Typography>
      )}
    </HeavyCard>
  );
};
